import { useState } from 'react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Textarea } from '../ui/textarea'
import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DescriptionPromptDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (promptTemplate: string) => void
  title: string
  author: string
}

// Prompt templates with {title} and {author} placeholders
interface PromptTemplate {
  id: string
  name: string
  description: string
  template: string
}

const PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: 'default',
    name: 'Default Blurb',
    description: 'Compelling book blurb with hook, conflict, and cliffhanger',
    template: `Generate a Book blurb for book "{title}" by {author} that will be added to my book store. up to 100 words.

Start with the hook: One strong sentence that tells the central conflict, question, or promise. It must make the reader curious. Describe the central tension without spoilers. Match the tone to the genre. Keep paragraphs short (2–4 lines). Use active, simple language. End with a cliffhanger or promise.

Check internet if needed.`
  }
]

export function DescriptionPromptDialog({ open, onClose, onConfirm, title, author }: DescriptionPromptDialogProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('default')
  const [customPrompt, setCustomPrompt] = useState<string>('')

  // Get the selected template
  const selectedTemplate = PROMPT_TEMPLATES.find(t => t.id === selectedTemplateId) || PROMPT_TEMPLATES[0]
  
  // Get the current prompt (either custom or from template)
  const currentPrompt = customPrompt || selectedTemplate.template

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId)
    // Reset custom prompt when selecting a template
    setCustomPrompt('')
  }

  const handleConfirm = () => {
    const finalPrompt = customPrompt || selectedTemplate.template
    onConfirm(finalPrompt)
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="description-prompt-dialog max-w-3xl max-h-[90vh] overflow-hidden flex flex-col [&>button]:hidden" 
        onInteractOutside={(e) => e.preventDefault()} 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Choose Description Prompt Template
          </DialogTitle>
          <DialogDescription>
            Select a prompt template or customize it. Use {'{title}'} and {'{author}'} as placeholders.
          </DialogDescription>
        </DialogHeader>

        {/* Template Tiles */}
        <div className="template-tiles grid grid-cols-1 md:grid-cols-3 gap-3 py-4">
          {PROMPT_TEMPLATES.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => handleTemplateSelect(template.id)}
              className={cn(
                'template-tile p-4 rounded-lg border-2 text-left transition-all',
                'hover:border-primary/50 hover:bg-accent',
                selectedTemplateId === template.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-background'
              )}
            >
              <div className="font-semibold text-sm mb-1">{template.name}</div>
              <div className="text-xs text-muted-foreground">{template.description}</div>
            </button>
          ))}
        </div>

        {/* Prompt Preview/Editor */}
        <div className="prompt-editor flex-1 flex flex-col min-h-0">
          <label className="text-sm font-medium mb-2">Prompt Template</label>
          <Textarea
            value={currentPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Enter your custom prompt template..."
            className="flex-1 min-h-[200px] font-mono text-sm"
          />
          <div className="text-xs text-muted-foreground mt-2">
            Preview with values: <code className="bg-muted px-1 rounded">{title}</code>, <code className="bg-muted px-1 rounded">{author}</code>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Generate Description
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

